import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';

/**
 * Fixtures for the employer student-loan CONTRIBUTIONS demo: an SLR-eligible
 * employee on a monthly contribution plan with ~6 months of paid contributions
 * (MOHELA + a historical Nelnet servicer), one failed + one resent payment for
 * status variety, and one upcoming payment.
 *
 * These are the model-shaped fixtures the SLR / contributions dashboard reads
 * once it runs through the store + request manager:
 *  - `store.query('accounting-transaction', { filter: { employee }, include:
 *    'accounting-line-item' })` → the contribution payment timeline. The
 *    contribution normalizer reads `amount` (cents), `state`, `transactionDate`,
 *    `servicer`, and `accountingLineItem.period`, so each transaction sideloads
 *    its line item.
 *  - `findRecord('employee', …, { include: 'plan,company' })` → the SLR plan
 *    (cadence !== 'NEVER' gates SLR eligibility) + company + company-setting.
 *  - raw fetch / adapter paths (seam B): `/accounting_preview` (future
 *    payments), `/people/{id}/slr-alerts` (account state + alerts), and
 *    `/api/v2/limits/status?product=SLR` (the SLR limits summary).
 *
 * NOTE: this PR lands the data layer + matchers only. The SLR/contributions
 * pages are still demo'd by `reimagined-data.ts`'s own `isDemoMode` branches
 * (e.g. `fetchContributionHistory` returns `getDemoContributionHistory()` and
 * never touches the store). Retiring those branches so these fixtures drive the
 * live render is the follow-up re-root PR.
 */

const SERVICER_MOHELA = 'MOHELA';
const SERVICER_NELNET = 'Nelnet';

// A paid contribution transaction + its sideloaded line item (carries the
// benefit-cycle period the normalizer reads). amount is in CENTS.
function contribution(
  id: string,
  amountCents: number,
  transactionDate: string | null,
  period: string,
  servicer: string,
  state: string
): { txn: JsonApiResource; lineItem: JsonApiResource } {
  const lineItemId = `${id}-line-item`;
  return {
    txn: {
      type: 'accounting-transaction',
      id,
      attributes: {
        amount: amountCents,
        state,
        transactionDate,
        servicer,
        description: 'Employer student loan contribution',
        isCheck: false,
      },
      relationships: {
        accountingLineItem: { data: { type: 'accounting-line-item', id: lineItemId } },
        employee: { data: { type: 'employee', id: 'demo-slr-employee-1' } },
      },
    },
    lineItem: {
      type: 'accounting-line-item',
      id: lineItemId,
      attributes: { amount: amountCents, period, date: transactionDate ?? `${period}-01` },
    },
  };
}

const CONTRIBUTIONS = [
  contribution('demo-contribution-1', 12500, '2026-05-14', '2026-05', SERVICER_MOHELA, 'sent'),
  contribution('demo-contribution-2', 12500, '2026-04-14', '2026-04', SERVICER_MOHELA, 'sent'),
  contribution('demo-contribution-3', 12500, '2026-03-14', '2026-03', SERVICER_MOHELA, 'sent'),
  // a failed + a resent payment (same cycle) for status variety
  contribution('demo-contribution-4', 12500, '2026-02-14', '2026-02', SERVICER_MOHELA, 'failed'),
  contribution('demo-contribution-5', 12500, '2026-02-28', '2026-02', SERVICER_MOHELA, 'resent'),
  // an older payment from a previous servicer
  contribution('demo-contribution-6', 10000, '2025-06-14', '2025-06', SERVICER_NELNET, 'sent'),
];

const PLAN_RESOURCE: JsonApiResource = {
  type: 'plan',
  id: 'demo-slr-plan-1',
  attributes: {
    name: 'Acme Student Loan Repayment',
    cadence: 'MONTHLY',
    annualLimit: 150000,
    lifetimeLimit: 1200000,
    currency: 'USD',
  },
};

// company-setting drives the employer-match (SECURE 2.0) card via
// modules.syf.isEnabled; this contributions-focused scenario has it off.
const COMPANY_SETTING_RESOURCE: JsonApiResource = {
  type: 'company-setting',
  id: 'demo-slr-companysetting-1',
  attributes: {
    modules: { syf: { isEnabled: false } },
  },
};

const COMPANY_RESOURCE: JsonApiResource = {
  type: 'company',
  id: 'demo-slr-company-1',
  attributes: { name: 'Acme Corporation', isActive: true },
  relationships: {
    companySetting: { data: { type: 'company-setting', id: 'demo-slr-companysetting-1' } },
  },
};

// Employee findRecord (include plan,company). The dashboard reads
// employee.plan.cadence to decide SLR eligibility, so the plan is sideloaded.
// Also seeded as the demo session identity (see DemoModeService.demoCurrentEmployee).
const EMPLOYEE_RESOURCE: JsonApiResource = {
  type: 'employee',
  id: 'demo-slr-employee-1',
  attributes: { firstName: 'Daffy', lastName: 'Duck', isActive: true },
  relationships: {
    plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
    company: { data: { type: 'company', id: 'demo-slr-company-1' } },
  },
};

const employeeDoc: JsonApiDoc = {
  data: EMPLOYEE_RESOURCE,
  included: [PLAN_RESOURCE, COMPANY_RESOURCE, COMPANY_SETTING_RESOURCE],
};

// --- Person + linked account (drives the contributions account cards) -------
// The SLR/contributions routes do findRecord('person', personId, {
//   include: 'accounts.financial-institution,accounts.loans,accounts.postal-address' })
// and read person.activeAccounts / person.accountReceivingContributions. All of
// account's relationships (financial-institution / loans / postal-address) are
// async:false, so they must be sideloaded. `accountReceivingContributionsId` is
// a plain attribute and must point at a seeded ACTIVE account. The handler
// echoes the requested (real-session) person id, so this fixture id is arbitrary.
const POSTAL_ADDRESS_RESOURCE: JsonApiResource = {
  type: 'postal-address',
  id: 'demo-slr-addr-1',
  attributes: {
    addressLine1: '633 Spirit Drive',
    city: 'Chesterfield',
    state: 'MO',
    postalCode: '63005',
    postalAddressType: 'PAYMENT',
    isActive: true,
  },
};

const FINANCIAL_INSTITUTION_RESOURCE: JsonApiResource = {
  type: 'financial-institution',
  id: 'demo-slr-fi-1',
  attributes: { name: SERVICER_MOHELA, isActive: true },
};

const LOAN_RESOURCE: JsonApiResource = {
  type: 'loan',
  id: 'demo-slr-loan-1',
  attributes: {
    isActive: true,
    observation: { balance: 4500000, 'next-payment-date': '2026-07-01' },
  },
  relationships: {
    account: { data: { type: 'account', id: 'demo-slr-account-1' } },
  },
};

const ACCOUNT_RESOURCE: JsonApiResource = {
  type: 'account',
  id: 'demo-slr-account-1',
  attributes: {
    status: 'ACTIVE',
    isActive: true,
    number: '****4321',
    publicOrdinalValue: 1,
    observation: {
      balance: 4500000,
      'interest-weighted-average': 5.5,
      'minimum-monthly-payment': 25000,
      'payable-account-number': '****4321',
      'provider-success-at': '2026-05-01T00:00:00Z',
      'is-paid-off': false,
    },
    summary: { loanCount: 1 },
  },
  relationships: {
    financialInstitution: { data: { type: 'financial-institution', id: 'demo-slr-fi-1' } },
    loans: { data: [{ type: 'loan', id: 'demo-slr-loan-1' }] },
    postalAddress: { data: { type: 'postal-address', id: 'demo-slr-addr-1' } },
    person: { data: { type: 'person', id: 'demo-slr-person-1' } },
  },
};

const PERSON_RESOURCE: JsonApiResource = {
  type: 'person',
  id: 'demo-slr-person-1',
  attributes: {
    firstName: 'Daffy',
    lastName: 'Duck',
    accountReceivingContributionsId: 'demo-slr-account-1',
  },
  relationships: {
    accounts: { data: [{ type: 'account', id: 'demo-slr-account-1' }] },
  },
};

const personDoc: JsonApiDoc = {
  data: PERSON_RESOURCE,
  included: [
    ACCOUNT_RESOURCE,
    FINANCIAL_INSTITUTION_RESOURCE,
    LOAN_RESOURCE,
    POSTAL_ADDRESS_RESOURCE,
  ],
};

const contributionsCollection: JsonApiDoc = {
  data: CONTRIBUTIONS.map((c) => c.txn),
  included: CONTRIBUTIONS.map((c) => c.lineItem),
};

export const CONTRIBUTIONS_MATCHERS: MatcherFixture[] = [
  { matcher: { kind: 'op', op: 'findRecord', type: 'employee' }, docs: [employeeDoc] },
  { matcher: { kind: 'op', op: 'findRecord', type: 'person' }, docs: [personDoc] },
  {
    matcher: { kind: 'op', op: 'query', type: 'accounting-transaction' },
    docs: [contributionsCollection],
  },
];

// --- Raw-fetch (seam B) matchers -------------------------------------------
// The contributions dashboard reaches these directly (bypassing the store):
//  - SLR limits summary (getV2SlrLimitsStatus → raw window.fetch) and SLR alerts
//    (api.call → raw window.fetch) are caught by the DemoModeService fetch
//    interceptor — no gating code needed.
//  - The future-payments (/accounting_preview) and past-payments
//    (/accounting_payments) endpoints go through the employee adapter's `ajax`,
//    which bypasses the request-manager demo handler, so the EmployeeAdapter
//    consults `demoJsonFor` for them (the tas-participant eligibility pattern).

// /employees/{id}/accounting_preview → upcoming contribution(s). amount in CENTS.
const FUTURE_PAYMENTS = [{ amount: 12500, period: '2026-06', validation_errors: [] }];

// GET /people/{id}/slr-alerts → page state + account status + alerts.
const SLR_ALERTS = {
  page_state: 'active',
  alerts: [],
  contribution_account: {
    id: 'demo-slr-account-1',
    servicer_name: SERVICER_MOHELA,
    account_number: '****4321',
    balance: 4500000,
    is_pif: false,
    servicer_address_type: null,
    status: 'ACTIVE',
  },
};

// GET /api/v2/limits/status?product=SLR → { data: [limitsData] }. Mirrors the
// shape getV2LimitsStatus unwraps (`parsed.data[0]`). $1,500 annual / $12,000
// lifetime, $700 remaining. Values in cents.
const SLR_ANNUAL_LIMIT = 150000;
const SLR_LIFETIME_LIMIT = 1200000;
const SLR_ANNUAL_USAGE = 80000;
const SLR_AVAILABLE = Math.max(
  0,
  Math.min(SLR_ANNUAL_LIMIT - SLR_ANNUAL_USAGE, SLR_LIFETIME_LIMIT - SLR_ANNUAL_USAGE)
);
const slrLimitDetail = {
  annual_limit: SLR_ANNUAL_LIMIT,
  annual_usage: SLR_ANNUAL_USAGE,
  available: SLR_AVAILABLE,
  balance_multiplier: 1,
  hard: true,
  limit: SLR_LIFETIME_LIMIT,
  one_month_limit: null,
  one_month_usage: 0,
  six_month_limit: null,
  six_month_usage: SLR_ANNUAL_USAGE,
  taxable_limit: null,
  three_month_limit: null,
  three_month_usage: SLR_ANNUAL_USAGE,
  usage: SLR_ANNUAL_USAGE,
  external_annual_usage: 0,
};
const SLR_LIMITS = {
  data: [
    {
      annual_start_date: '2026-01-01',
      annual_end_date: '2026-12-31',
      as_of_date: '2026-06-05',
      available: SLR_AVAILABLE,
      available_with_override: SLR_AVAILABLE,
      details: {
        company: slrLimitDetail,
        product: slrLimitDetail,
        batch: slrLimitDetail,
        scheme: slrLimitDetail,
      },
      employee_id: 'demo-slr-employee-1',
      hard_limit: 'scheme_annual',
      limit_capacity: 'TAS.LimitCapacity.MONETARY',
      product: 'SLR',
      scheme: 'demo-slr-plan-1',
      soft_limit: 'scheme_annual',
      external_annual_usage: 0,
    },
  ],
};

// GET /employees/{id}/accounting_payments → past employer-contribution payments
// + a summary. `summary.total` (cents) MUST be defined: fetchEmployerContributions
// gates `isLiveData` on it and otherwise falls back to the career-pathways demo
// snapshot. The displayed YTD/lifetime are recomputed from the accounting-transaction
// timeline, so an empty `payments` here is fine (the timeline is the source of truth).
const ACCOUNTING_PAYMENTS = { payments: [], summary: { total: 60000 } };

export const CONTRIBUTIONS_FETCH_MATCHERS: FetchMatcher[] = [
  { urlIncludes: '/accounting_preview', json: FUTURE_PAYMENTS },
  { urlIncludes: '/accounting_payments', json: ACCOUNTING_PAYMENTS },
  { urlIncludes: '/slr-alerts', json: SLR_ALERTS },
  {
    urlMatches: (url) => url.includes('/api/v2/limits/status') && url.includes('product=SLR'),
    json: SLR_LIMITS,
  },
];
